import { useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmployeeIDPView } from '../development/EmployeeIDPView'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'

interface IDPViewerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  memberName: string
  memberId: string
  memberRole?: string
  /** Read without changing — for anyone who is not this person's People Leader. */
  readOnly?: boolean
}

/**
 * Manager's view of an employee's IDP.
 * This is a simple wrapper around EmployeeIDPView which handles both employee and manager roles.
 */
export default function IDPViewerDialog({
  open,
  onOpenChange,
  memberName,
  memberId,
  memberRole,
  readOnly = false,
}: IDPViewerDialogProps) {
  // What the People Leader acknowledged here changes the plan gaps the Talent
  // tab lists; re-read them when the dialog closes.
  const queryClient = useQueryClient()
  const handleOpenChange = (next: boolean) => {
    if (!next) queryClient.invalidateQueries({ queryKey: ['talent'] })
    onOpenChange(next)
  }
  // Convert string ID to numeric if it's a number
  const numericId = !isNaN(Number(memberId)) ? Number(memberId) : undefined
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{memberName}'s Growth Plan</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        <div className="overflow-y-auto max-h-[78vh]">
          <EmployeeIDPView 
            userId={memberId}
            numericUserId={numericId}
            userRole="manager"
            readOnly={readOnly}
            employeeName={memberName}
            employeeRole={memberRole}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
